import { useCallback, useEffect, useRef, useState } from "react";
import {
  deletePlaceImage,
  deletePlaceTrack,
  getApiErrorMessage,
  getPlaceTrack,
  isAccessRevokedError,
  isAccommodationPlaceProperties,
  isBackcountryPlaceProperties,
  isHikingPlaceProperties,
  isBikeParkPlaceProperties,
  isMtbTrailPlaceProperties,
  isMtbTrailRelatedPlace,
  isNotFoundError,
  PLACE_TYPE_META,
  type PlaceImage,
  type PlacePreviewProperties,
  type PlaceType,
  type RelatedPlace,
  type UpdatePlaceData,
  uploadPlaceImage,
  uploadPlaceTrack,
} from "~/lib/api";
import { cn } from "~/lib/utils";
import { ActionsMenu } from "~/components/ui/actions-menu";
import { Button } from "~/components/ui/button";
import { Icon } from "~/components/ui/icons/icon";
import { PlaceTypeIcon } from "~/components/ui/icons/place-type-icons";
import { ImageDropzone } from "~/components/ui/image-dropzone";
import { ImageThumbnail } from "~/components/ui/image-thumbnail";
import { AccommodationInfoSection } from "~/components/place/accommodation/AccommodationInfoSection";
import { BackcountryInfoSection } from "~/components/place/backcountry/BackcountryInfoSection";
import { BikeParkInfoSection } from "~/components/place/mtb/BikeParkInfoSection";
import { BackcountryRelatedSection } from "~/components/place/backcountry/BackcountryRelatedSection";
import { BikeParkRelatedSection } from "~/components/place/mtb/BikeParkRelatedSection";
import { ImageGallery } from "~/components/place/ImageGallery";
import { ImageLightbox } from "~/components/place/ImageLightbox";
import { MtbTrailInfoSection } from "~/components/place/mtb/MtbTrailInfoSection";
import { PricingSection } from "~/components/place/pricing/PricingSection";
import { MtbTrailStyleSection } from "~/components/place/mtb/MtbTrailStyleSection";
import { TrailInfoSection } from "~/components/place/hiking/TrailInfoSection";
import { EditableInput, EditableTextarea, EditableList } from "~/components/ui/inline-edit";
import { PanelSection } from "~/components/ui/panel-section";
import { useImageUpload } from "~/hooks/useImageUpload";

const PLACE_TYPES = Object.entries(PLACE_TYPE_META) as [PlaceType, { label: string }][];

import type { DetailLevel } from "~/lib/detailLevel";

interface PlaceDetailViewProps {
  place: PlacePreviewProperties;
  onBack: () => void;
  /** Accessible label for the back button */
  backLabel?: string;
  /** Controls gallery height and content visibility based on available space */
  details?: DetailLevel;
  /** When true, hides all edit actions (for public viewer) */
  readOnly?: boolean;
  /** When provided, shows "Add to map" mode instead of edit/delete actions */
  onAdd?: () => Promise<void>;
  onUpdate?: (data: UpdatePlaceData) => Promise<void>;
  onDelete?: () => Promise<void>;
  onPublish?: () => Promise<void>;
  onTransfer?: () => void;
  onImageAdded?: (image: PlaceImage) => void;
  onImageDeleted?: (imageId: string) => void;
  onTrackUploaded?: (track: GeoJSON.LineString) => void;
  onTrackDeleted?: () => void;
  /** Called when an operation fails due to access being revoked (403/404) */
  onAccessRevoked?: () => void;
  /** When true, focuses the container on mount (for keyboard navigation) */
  autoFocus?: boolean;
  /** When true, shows edit affordances for related places */
  canEditRelatedPlaces?: boolean;
  onRelatedPlaceRemove?: (relatedPlaceId: string) => Promise<void>;
  onSelectRelatedPlace?: (place: RelatedPlace) => void;
}

export function PlaceDetailView({
  place,
  onBack,
  backLabel = "Back to map",
  details = "full",
  readOnly,
  onAdd,
  onUpdate,
  onDelete,
  onPublish,
  onTransfer,
  onImageAdded,
  onImageDeleted,
  onTrackUploaded,
  onTrackDeleted,
  onAccessRevoked,
  autoFocus,
  canEditRelatedPlaces,
  onRelatedPlaceRemove,
  onSelectRelatedPlace,
}: PlaceDetailViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus container on mount when autoFocus is true (keyboard navigation)
  useEffect(() => {
    if (autoFocus) {
      containerRef.current?.focus();
    }
  }, [autoFocus]);
  const { id: placeId, permissions, name, type, description, links, images, email, phone } = place;
  const mtbMetadata = isMtbTrailPlaceProperties(place) ? place.metadata : null;
  const bikeParkMetadata = isBikeParkPlaceProperties(place) ? place.metadata : null;

  const [deleting, setDeleting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [error, setError] = useState("");

  type FieldKey = "name" | "description" | "email" | "phone" | "links";
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [fieldSaving, setFieldSaving] = useState<Partial<Record<FieldKey, boolean>>>({});

  const setFieldError = (field: FieldKey, error: string | undefined) => {
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const setFieldSavingState = (field: FieldKey, saving: boolean) => {
    setFieldSaving((prev) => ({ ...prev, [field]: saving }));
  };

  const createFieldSaveHandler = <T,>(field: FieldKey, getData: (value: T) => UpdatePlaceData) => {
    return async (value: T) => {
      if (!onUpdate) return;
      setFieldSavingState(field, true);
      setFieldError(field, undefined);
      try {
        await onUpdate(getData(value));
      } catch (err) {
        if (isAccessRevokedError(err)) {
          onAccessRevoked?.();
        } else {
          setFieldError(field, getApiErrorMessage(err, "Failed to save"));
        }
        throw err; // Re-throw so EditableInput stays in edit mode
      } finally {
        setFieldSavingState(field, false);
      }
    };
  };

  const clearFieldError = (field: FieldKey) => () => setFieldError(field, undefined);

  const canEdit = !readOnly && permissions?.canEdit;
  const canUploadImages = canEdit;

  const handleTrackUpload =
    canEdit && onTrackUploaded
      ? async (file: File) => {
          const track = await uploadPlaceTrack(placeId, file);
          onTrackUploaded(track);
        }
      : undefined;

  const handleTrackDelete =
    canEdit && onTrackDeleted
      ? async () => {
          await deletePlaceTrack(placeId);
          onTrackDeleted();
        }
      : undefined;

  const handleUpload = useCallback(async (file: Blob) => uploadPlaceImage(placeId, file), [placeId]);

  const handleUploadError = useCallback(
    (err: Error) => {
      if (isAccessRevokedError(err)) {
        onAccessRevoked?.();
      }
      // Default toast.error is handled by useImageUpload
    },
    [onAccessRevoked],
  );

  const { upload, uploading } = useImageUpload({
    uploadFn: handleUpload,
    onSuccess: onImageAdded,
    onError: handleUploadError,
  });

  const handleDeleteImage = async (imageId: string) => {
    setDeletingImageId(imageId);
    try {
      await deletePlaceImage(placeId, imageId);
      onImageDeleted?.(imageId);
    } catch (err) {
      if (isNotFoundError(err)) {
        // Image already deleted by another collaborator - treat as success
        onImageDeleted?.(imageId);
      } else if (isAccessRevokedError(err)) {
        onAccessRevoked?.();
      } else {
        setError("Failed to delete image");
      }
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    setError("");
    try {
      await onDelete();
    } catch (err) {
      if (isAccessRevokedError(err)) {
        onAccessRevoked?.();
      } else {
        setError("Failed to remove from map");
      }
    } finally {
      setDeleting(false);
    }
  };

  const handlePublish = async () => {
    if (!onPublish) return;
    if (!confirm("Publish this place to the Noma database? It will become publicly discoverable.")) return;
    setPublishing(true);
    setError("");
    try {
      await onPublish();
    } catch (err) {
      if (isAccessRevokedError(err)) {
        onAccessRevoked?.();
      } else {
        setError("Failed to publish place");
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleAdd = async () => {
    if (!onAdd) return;
    setAdding(true);
    setError("");
    try {
      await onAdd();
    } catch {
      setError("Failed to add place");
    } finally {
      setAdding(false);
    }
  };

  // Show hero gallery only in read mode (readOnly or onAdd mode) and not minimal
  const showHeroGallery = !canEdit && images.length > 0 && details !== "minimal";

  return (
    <div ref={containerRef} tabIndex={-1} className="flex flex-col gap-4 pb-4 outline-none">
      {/* Hero image gallery - read mode only, hidden in minimal */}
      {showHeroGallery && (
        <ImageGallery
          images={images}
          placeName={name}
          onClick={(index) => {
            setLightboxIndex(index);
            setLightboxOpen(true);
          }}
          className={cn({
            "h-40": details === "compact",
            "h-64": details === "full",
          })}
        />
      )}

      {/* Fullscreen lightbox */}
      <ImageLightbox
        images={images}
        placeName={name}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {/* Header */}
      <div
        className={cn("px-6 transition-[padding] duration-200 ease-out", {
          "pt-0": showHeroGallery,
          "pt-1": details === "minimal",
          "pt-4": !showHeroGallery && details !== "minimal",
        })}
      >
        <div className="flex items-start gap-3">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onBack}
            className="mt-0.5"
            aria-label={backLabel}
            title={backLabel}
          >
            <Icon i="back" className="size-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <EditableInput
              value={name}
              onSave={createFieldSaveHandler("name", (v: string | null) => ({ name: v! }))}
              onChange={clearFieldError("name")}
              error={fieldErrors.name}
              saving={fieldSaving.name}
              placeholder="Place name"
              showIcon={false}
              required
              canEdit={canEdit}
              className="text-lg font-medium text-foreground"
            />
            {type && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                <PlaceTypeIcon type={type} className="size-4" />
                <span>
                  {PLACE_TYPE_META[type].label}
                  {place.pricing?.free ? " · Free" : ""}
                </span>
              </div>
            )}
          </div>
          <ActionsMenu
            actions={[
              [
                canEdit && {
                  icon: type ?? "marker",
                  label: "Change type",
                  subActions: PLACE_TYPES.map(([placeType, { label }]) => ({
                    icon: placeType,
                    label,
                    disabled: placeType === type,
                    onClick: () => onUpdate?.({ type: placeType }),
                  })),
                },
                onPublish && {
                  icon: "external-link",
                  label: publishing ? "Publishing..." : "Publish to noma.to",
                  disabled: publishing,
                  onClick: handlePublish,
                },
              ],
              [
                onTransfer && {
                  icon: "transfer" as const,
                  label: "Transfer ownership",
                  onClick: onTransfer,
                  variant: "destructive" as const,
                },
              ],
            ]}
          />
        </div>
      </div>

      {/* Add/Remove action button */}
      {(onAdd || onDelete) && (
        <PanelSection.Root>
          <PanelSection.Content>
            {onAdd ? (
              <Button variant="outline" onClick={handleAdd} disabled={adding} className="w-full">
                <PlaceTypeIcon type={type} className="size-4 text-primary" />
                {adding ? "Adding..." : "Add to map"}
              </Button>
            ) : (
              <Button variant="outline" onClick={handleDelete} disabled={deleting} className="w-full">
                <PlaceTypeIcon type={type} className="size-4 text-primary" />
                {deleting ? "Removing..." : "Remove from map"}
              </Button>
            )}
          </PanelSection.Content>
        </PanelSection.Root>
      )}

      {/* Description */}
      {(description || canEdit) && (
        <PanelSection.Root>
          <PanelSection.Content>
            <EditableTextarea
              value={description}
              onSave={createFieldSaveHandler("description", (v: string | null) => ({ description: v }))}
              onChange={clearFieldError("description")}
              error={fieldErrors.description}
              saving={fieldSaving.description}
              placeholder="Add a description..."
              addLabel="+ Add description"
              canEdit={canEdit}
            />
          </PanelSection.Content>
        </PanelSection.Root>
      )}

      {/* Pricing - all place types */}
      <PricingSection pricing={place.pricing} placeType={type} canEdit={canEdit} onUpdate={onUpdate} />

      {/* Trail Info - hiking places only */}
      {type === "hiking" && (
        <TrailInfoSection
          metadata={isHikingPlaceProperties(place) ? place.metadata : null}
          canEdit={canEdit}
          onUpdate={onUpdate}
          track={getPlaceTrack(place)}
          onTrackUpload={handleTrackUpload}
          onTrackDelete={handleTrackDelete}
        />
      )}

      {type === "mtb-trail" && (
        <>
          <MtbTrailInfoSection
            metadata={mtbMetadata}
            canEdit={canEdit}
            onUpdate={onUpdate}
            track={getPlaceTrack(place)}
            onTrackUpload={handleTrackUpload}
            onTrackDelete={handleTrackDelete}
          />
          <MtbTrailStyleSection metadata={mtbMetadata} canEdit={canEdit} onUpdate={onUpdate} />
        </>
      )}

      {type === "bike-park" && (
        <>
          <BikeParkInfoSection
            metadata={bikeParkMetadata}
            trails={(place.relatedPlaces ?? []).filter(isMtbTrailRelatedPlace)}
            canEdit={canEdit}
            onUpdate={onUpdate}
          />
          <BikeParkRelatedSection
            placeId={placeId}
            relatedPlaces={place.relatedPlaces ?? []}
            canEdit={canEditRelatedPlaces}
            onRemove={onRelatedPlaceRemove}
            onSelect={onSelectRelatedPlace}
          />
        </>
      )}

      {/* Backcountry Info - backcountry places only */}
      {type === "backcountry" && (
        <>
          <BackcountryInfoSection
            metadata={isBackcountryPlaceProperties(place) ? place.metadata : null}
            canEdit={canEdit}
            onUpdate={onUpdate}
            track={getPlaceTrack(place)}
            onTrackUpload={handleTrackUpload}
            onTrackDelete={handleTrackDelete}
          />
          <BackcountryRelatedSection
            placeId={placeId}
            relatedPlaces={place.relatedPlaces ?? []}
            canEdit={canEditRelatedPlaces}
            onRemove={onRelatedPlaceRemove}
            onSelect={onSelectRelatedPlace}
          />
        </>
      )}

      {type === "accommodation" && (
        <AccommodationInfoSection
          metadata={isAccommodationPlaceProperties(place) ? place.metadata : null}
          canEdit={canEdit}
          onUpdate={onUpdate}
        />
      )}

      {/* Photos section - edit mode: thumbnails with delete + dropzone */}
      {(canEdit && images.length > 0) || canUploadImages ? (
        <>
          <PanelSection.Separator />
          <PanelSection.Root>
            <PanelSection.Title>Photos</PanelSection.Title>
            <PanelSection.Content>
              {canEdit && images.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pt-2 pr-2 pb-2 touch-pan-x">
                  {images.map((image) => (
                    <ImageThumbnail
                      key={image.id}
                      src={image.thumb}
                      srcSet={image.srcset}
                      sizes="120px"
                      alt={`Photo of ${name}`}
                      onDelete={() => handleDeleteImage(image.id)}
                      disabled={deletingImageId === image.id}
                      className="size-24"
                    />
                  ))}
                </div>
              )}
              {canUploadImages && (
                <ImageDropzone
                  onFilesSelect={(files) => files.forEach(upload)}
                  multiple
                  disabled={uploading}
                  className={images.length > 0 ? "mt-2" : undefined}
                />
              )}
            </PanelSection.Content>
          </PanelSection.Root>
        </>
      ) : null}

      {/* Contact section */}
      {(email || phone || canEdit) && (
        <>
          <PanelSection.Separator />
          <PanelSection.Root>
            <PanelSection.Title>Contact</PanelSection.Title>
            <PanelSection.Content>
              <EditableInput
                type="email"
                value={email}
                onSave={createFieldSaveHandler("email", (v: string | null) => ({ email: v }))}
                onChange={clearFieldError("email")}
                error={fieldErrors.email}
                saving={fieldSaving.email}
                placeholder="email@example.com"
                addLabel="+ Add email"
                canEdit={canEdit}
              />
              <EditableInput
                type="phone"
                value={phone}
                onSave={createFieldSaveHandler("phone", (v: string | null) => ({ phone: v }))}
                onChange={clearFieldError("phone")}
                error={fieldErrors.phone}
                saving={fieldSaving.phone}
                placeholder="+1 234 567 8900"
                addLabel="+ Add phone"
                canEdit={canEdit}
              />
            </PanelSection.Content>
          </PanelSection.Root>
        </>
      )}

      {/* Links section */}
      {(links.length > 0 || canEdit) && (
        <>
          <PanelSection.Separator />
          <PanelSection.Root>
            <PanelSection.Title>Links</PanelSection.Title>
            <PanelSection.Content>
              <EditableList
                values={links}
                onSave={createFieldSaveHandler("links", (v: string[]) => ({ links: v }))}
                onChange={clearFieldError("links")}
                error={fieldErrors.links}
                saving={fieldSaving.links}
                placeholder="https://..."
                addLabel="+ Add link"
                canEdit={canEdit}
              />
            </PanelSection.Content>
          </PanelSection.Root>
        </>
      )}

      {/* Non-field errors */}
      {error && (
        <PanelSection.Root>
          <PanelSection.Content>
            <p className="text-destructive text-sm">{error}</p>
          </PanelSection.Content>
        </PanelSection.Root>
      )}
    </div>
  );
}
